import { Close, PendingActions, Save, Info, DesignServices } from '@mui/icons-material';
import { Grid, Box, Button, ButtonGroup, Tooltip } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FC, useContext, useState } from 'react';
import {
  approveContractDocuments,
  downloadContractDocument,
  downloadContractDocumentPacket,
  regenerateContractDocuments,
  finishSpec,
  unapproveContract,
  getBuildById,
  generateContractDocuments
} from '../../../fetch';
import { Card, Button as SMButton } from '../../../components';
import {
  ISpec,
  DocumentContractingType,
  IContractDocumentResults,
  BuildDocumentType,
  BuildStatus,
  ISpecOptionsDetail
} from '../../../models';
import { useSnackbar } from 'notistack';
import { downloadFile, getDownloadFileName } from '../../../helpers';
import { Form, useFormikContext } from 'formik';
import { useHistory } from 'react-router-dom';
import { UserContext } from '../../../context';
import { GenerateContractModal } from './GenerateContractModal';
import { useFlags } from '../../../hooks';
import { ContractDocumentsUploads } from './contract-documents-uploads';

interface IContractDocuments {
  spec: ISpec;
  specOptions: ISpecOptionsDetail;
  isSaving: boolean;
  setSaving: (isSaving: boolean) => void;
  onClose: () => void;
  isAdmin: boolean;
  isFinishing: boolean;
  setIsFinishing: (isFinishing: boolean) => void;
  redirect?: string;
  path?: string;
  isLoading: boolean;
  isRefetching: boolean;
  contractDocumentResults: IContractDocumentResults;
  refetchContractDocumentResults: any;
  shouldShowFinishSpecButton: boolean;
}

export const ContractDocuments: FC<IContractDocuments> = ({
  spec,
  specOptions,
  isSaving,
  setSaving,
  onClose,
  isAdmin,
  isFinishing,
  setIsFinishing,
  redirect,
  path,
  isLoading,
  isRefetching,
  contractDocumentResults,
  refetchContractDocumentResults,
  shouldShowFinishSpecButton = true
}) => {
  const { user } = useContext(UserContext);
  const isClient = user?.userRoles?.includes('BL_Client');
  const isAgent = user?.userRoles?.includes('BL_Agent');
  const isSalesAdmin = user?.userRoles?.includes('BL_SalesAdmin');
  const [isUnapproving, setIsUnapproving] = useState<boolean>(false);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const [modalOpen, setModalOpen] = useState(false);
  const [contractingType, setContractingType] = useState<string>('');
  const featureFlags = useFlags();

  const isSalesDigitalContracting = featureFlags.salesDigitalContracting;

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  const history = useHistory();
  const handleDownloadDocument = async (buildDocumentId: number, fileName: string) => {
    try {
      const res = await downloadContractDocument(buildDocumentId);
      downloadFile(res?.data, getDownloadFileName(res) || fileName);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error downloading document, please try again.`, {
        variant: 'error'
      });
    }
  };

  const handleDownloadDocumentPackage = async (buildId: number) => {
    try {
      const res = await downloadContractDocumentPacket(buildId);
      downloadFile(res, getDownloadFileName(res) || `${buildId}-Contract-Documents`);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error downloading document package, please try again.`, {
        variant: 'error'
      });
    }
  };

  const handleBuildContractUpdate = async (id: string | number) => {
    try {
      const res = await getBuildById(id);
      spec.canUnapproveBuildContract = res.canUnapproveBuildContract;
      spec.canApproveBuildContract = res.canApproveBuildContract;
      spec.buildStatus = res.buildStatus;
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error updating contract, please try again.`, {
        variant: 'error'
      });
    }
  };

  // Connects to the parent formik context, https://formik.org/docs/api/useFormikContext#reference
  // As of 04/16/2024, that formik context is in the SelectionsDetailsInfo.tsx form.
  const { handleSubmit, isSubmitting, isValid } = useFormikContext();

  // This is a check to see if the build is in a test status
  const isTestBuild = spec?.buildStatus === BuildStatus.Test ?? false;

  const buildStatusIsAfterContractIsAccepted =
    (spec?.buildStatus === BuildStatus.ContractAccepted ||
      spec?.buildStatus === BuildStatus.TenDayComplete ||
      spec?.buildStatus === BuildStatus.Closing) ??
    false;
  return (
    <>
      <Box displayPrint="none">
        <Form onSubmit={handleSubmit} autoComplete="none">
          <Card icon={<PendingActions />} title="Documents">
            <Grid container spacing={1} paddingTop={0.5}>
              {isSalesDigitalContracting && !isTestBuild && (
                <ContractDocumentsUploads
                  spec={spec}
                  contractDocumentResults={contractDocumentResults}
                  refetchContractDocumentResults={refetchContractDocumentResults}
                />
              )}

              {contractDocumentResults?.contractDocuments?.length !== 0 &&
                !contractDocumentResults?.hasHouseContingencyWithoutAddendumK && (
                  <Grid item xs={12}>
                    <Grid container spacing={1}>
                      {contractDocumentResults?.contractDocuments?.map(document => (
                        <Grid key={document?.name} item xs={12} lg={6} textAlign={'center'}>
                          <Button
                            onClick={() =>
                              handleDownloadDocument(document?.buildDocumentId, document?.name)
                            }
                          >
                            {document?.name}
                          </Button>
                        </Grid>
                      ))}
                      {contractDocumentResults?.contractDocuments.find(
                        doc => doc.buildDocumentTypeId === BuildDocumentType.Contract
                      ) && (
                        <Grid item xs={12} textAlign={'center'}>
                          <Button onClick={() => handleDownloadDocumentPackage(spec.buildId)}>
                            Download Contract Package
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}

              <Box sx={theme => ({ padding: theme.spacing(1) })} display={'flex'} gap={1}>
                {!isClient &&
                  (contractDocumentResults?.contractDocuments?.length === 0 ||
                    contractDocumentResults?.generateRequired) &&
                  spec.missingContractCategories === null && (
                    <Box>
                      <LoadingButton
                        loading={isSaving}
                        loadingPosition="start"
                        variant="contained"
                        color="primary"
                        disabled={isLoading || isRefetching || isSaving}
                        startIcon={<Save />}
                        onClick={async () => {
                          if (featureFlags.salesDigitalContracting && !isTestBuild) {
                            handleOpen();
                          } else {
                            try {
                              setSaving(true);
                              await generateContractDocuments(
                                spec.buildId,
                                DocumentContractingType.Scan
                              );
                              setContractingType(DocumentContractingType.Scan);
                              await refetchContractDocumentResults();
                            } catch (error) {
                              enqueueSnackbar(
                                error?.Detail ??
                                  `Error generating contract documents, please try again.`,
                                {
                                  variant: 'error'
                                }
                              );
                            } finally {
                              setSaving(false);
                            }
                          }
                        }}
                      >
                        Generate Contract
                      </LoadingButton>
                    </Box>
                  )}

                {!isClient &&
                  (spec?.canApproveBuildContract || isTestBuild) &&
                  contractDocumentResults?.contractDocuments?.length !== 0 &&
                  !contractDocumentResults?.generateRequired && (
                    <Box>
                      <LoadingButton
                        loading={isRegenerating}
                        loadingPosition="start"
                        variant="contained"
                        color="primary"
                        disabled={
                          isLoading ||
                          isRefetching ||
                          isSaving ||
                          !(
                            !isClient &&
                            (spec?.canApproveBuildContract || isTestBuild) &&
                            contractDocumentResults?.contractDocuments?.length !== 0 &&
                            !contractDocumentResults?.generateRequired
                          )
                        }
                        startIcon={<Save />}
                        onClick={async () => {
                          try {
                            setSaving(true);
                            setIsRegenerating(true);
                            await regenerateContractDocuments(spec.buildId);
                            await refetchContractDocumentResults();
                            enqueueSnackbar(`Contract Selections Regenerated!`, {
                              variant: 'success'
                            });
                          } catch (error) {
                            enqueueSnackbar(
                              error?.Detail ??
                                `Error regenerating contract selections, please try again.`,
                              {
                                variant: 'error'
                              }
                            );
                          } finally {
                            setSaving(false);
                            setIsRegenerating(false);
                          }
                        }}
                      >
                        Regenerate Contract
                      </LoadingButton>
                    </Box>
                  )}

                {!isClient &&
                  spec?.canApproveBuildContract &&
                  !isTestBuild &&
                  (isAdmin || isSalesAdmin) && (
                    <Box>
                      <Tooltip
                        title={
                          contractDocumentResults?.regenerateRequired
                            ? 'To approve build, please regenerate the contract file.'
                            : ''
                        }
                        placement="top"
                      >
                        <span>
                          <Button
                            type="button"
                            startIcon={<Save />}
                            variant="contained"
                            color="primary"
                            disabled={
                              isLoading ||
                              isRefetching ||
                              isSaving ||
                              contractDocumentResults?.generateRequired ||
                              contractDocumentResults?.regenerateRequired ||
                              ((contractingType === DocumentContractingType.Digital ||
                                spec?.salesContractingType === DocumentContractingType.Digital ||
                                contractDocumentResults.salesContractingType ===
                                  DocumentContractingType.Digital) &&
                                !contractDocumentResults?.hasCompletedContract)
                            }
                            onClick={async () => {
                              try {
                                setSaving(true);
                                await approveContractDocuments(spec.buildId);
                                await handleBuildContractUpdate(spec.buildId);
                                spec.isContractComplete = true;
                                enqueueSnackbar(`Contract Selections Approved!`, {
                                  variant: 'success'
                                });
                                await refetchContractDocumentResults();
                                //Redirect on Success
                                history.push(
                                  `/sales/contracting/contract-acceptance/${spec.buildId}`
                                );
                              } catch (error) {
                                enqueueSnackbar(
                                  error?.Detail ??
                                    `Error approving contract selections, please try again.`,
                                  {
                                    variant: 'error'
                                  }
                                );
                              } finally {
                                setSaving(false);
                              }
                            }}
                          >
                            Approve Contract
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                {!isClient && spec?.canUnapproveBuildContract && (isAdmin || isSalesAdmin) && (
                  <Box>
                    <LoadingButton
                      loading={isUnapproving}
                      loadingPosition="start"
                      variant="contained"
                      color="error"
                      disabled={isLoading || isSaving}
                      startIcon={<Close />}
                      onClick={async () => {
                        try {
                          setSaving(true);
                          setIsUnapproving(true);
                          await unapproveContract(spec.buildId);
                          await handleBuildContractUpdate(spec.buildId);
                          enqueueSnackbar(`Contract Successfully Unapproved!`, {
                            variant: 'success'
                          });
                          refetchContractDocumentResults();
                        } catch (error) {
                          enqueueSnackbar(
                            error?.Detail ?? `Error unapproving contract, please try again.`,
                            {
                              variant: 'error'
                            }
                          );
                        } finally {
                          setSaving(false);
                          setIsUnapproving(false);
                        }
                      }}
                    >
                      Unapprove Contract
                    </LoadingButton>
                  </Box>
                )}
                {!isClient &&
                  !buildStatusIsAfterContractIsAccepted &&
                  (spec?.salesContractingType === DocumentContractingType.Digital ||
                    contractDocumentResults?.salesContractingType ===
                      DocumentContractingType.Digital) && (
                    <Box>
                      <Button
                        startIcon={<DesignServices />}
                        type={'button'}
                        variant="contained"
                        color="primary"
                        disabled={
                          !(
                            contractingType === DocumentContractingType.Digital ||
                            contractDocumentResults?.salesContractingType ===
                              DocumentContractingType.Digital
                          )
                        }
                        onClick={() => {
                          history.push(
                            `/sales/contracting/contract-markup?buildId=${spec?.buildId}&neighborhood=${spec?.neighborhoodDescription}&lot=${spec?.lotNumber}`
                          );
                        }}
                      >
                        Digital Contract Markup
                      </Button>
                    </Box>
                  )}
              </Box>
            </Grid>
          </Card>
          <ButtonGroup sx={{ marginTop: '1rem' }}>
            <Button
              disabled={isFinishing}
              onClick={() => onClose()}
              startIcon={<Close />}
              variant="contained"
              color="inherit"
            >
              Back to Build Options
            </Button>
            {!isClient && !isTestBuild && (
              <SMButton
                id="selections-details-link"
                color="secondary"
                variant="contained"
                startIcon={<Info />}
                asLink
                to={`/sales/contracting/contract-acceptance/${spec?.buildId}`}
              >
                {'Contract Acceptance Details'}
              </SMButton>
            )}
            {spec?.isSpec && isAdmin && shouldShowFinishSpecButton && (
              <Button
                disabled={isFinishing}
                onClick={async () => {
                  try {
                    setIsFinishing(true);
                    await finishSpec(spec.buildId);
                    history.push(redirect ?? `${path ?? '/development'}/specs`);
                    enqueueSnackbar('Spec finished!', {
                      variant: 'success'
                    });
                  } catch (error) {
                    enqueueSnackbar(error?.Detail ?? `Error finishing spec, please try again.`, {
                      variant: 'error'
                    });
                  } finally {
                    setIsFinishing(false);
                  }
                }}
                startIcon={<Save />}
                variant="contained"
                color="primary"
              >
                Finish Spec
              </Button>
            )}
            {spec?.isContractComplete &&
              !spec?.isTenDayComplete &&
              spec.missingTenDayCategories === null &&
              !isClient &&
              !isAgent && (
                // We no longer need to check if this is dirty, since it could load in a valid state.
                // If it does not load in a valid state, then the user will not be able to submit the form.
                <Button
                  disabled={isSubmitting || !isValid}
                  type="submit"
                  startIcon={<Save />}
                  variant="contained"
                  color="primary"
                >
                  Complete Build
                </Button>
              )}
          </ButtonGroup>
        </Form>
      </Box>
      <GenerateContractModal
        open={modalOpen}
        handleClose={handleClose}
        setSaving={setSaving}
        refetchContractDocumentResults={refetchContractDocumentResults}
        specBuildId={spec?.buildId}
        setContractingType={setContractingType}
      />
    </>
  );
};
